import {
  CANVAS_ZOOM_MAX,
  CANVAS_ZOOM_MIN,
  FIT_TO_SPREAD_VIEW_PADDING_PX,
} from '../constants/canvas-zoom'

/**
 * Fit-to-spread scale: the document fitted inside the padded viewport, clamped to the zoom range.
 * A degenerate ratio (non-finite or non-positive) falls back to the minimum zoom.
 */
export function computeFitSpreadScale(
  viewportWidth: number,
  viewportHeight: number,
  docW: number,
  docH: number
): number {
  const pad = FIT_TO_SPREAD_VIEW_PADDING_PX
  const availW = Math.max(1, viewportWidth - pad * 2)
  const availH = Math.max(1, viewportHeight - pad * 2)
  const raw = Math.min(availW / docW, availH / docH)

  return Number.isFinite(raw) && raw > 0
    ? Math.min(CANVAS_ZOOM_MAX, Math.max(CANVAS_ZOOM_MIN, raw))
    : CANVAS_ZOOM_MIN
}

/** Scale at which an axis-aligned canvas-space rect fills the viewport (with padding). */
export function computeZoomToRectScale(
  viewportWidth: number,
  viewportHeight: number,
  b: { width: number; height: number }
): number {
  const pad = 48
  const availW = Math.max(1, viewportWidth - pad * 2)
  const availH = Math.max(1, viewportHeight - pad * 2)
  const scaleW = availW / b.width
  const scaleH = availH / b.height

  return Math.min(CANVAS_ZOOM_MAX, Math.max(CANVAS_ZOOM_MIN, Math.min(scaleW, scaleH)))
}

/** Scale at which the full bleed width fits the viewport width (with padding). */
export function computeFitWidthScale(viewportWidth: number, bleedWidth: number): number {
  const pad = 32
  const avail = Math.max(1, viewportWidth - pad * 2)

  return Math.min(CANVAS_ZOOM_MAX, Math.max(CANVAS_ZOOM_MIN, avail / bleedWidth))
}

/** Same step as `EDITOR_ZOOM_ADDITIVE_STEP` in `home.tsx` (toolbar / keyboard). */
const TOOLBAR_ZOOM_ADDITIVE_STEP = 0.1

/**
 * Next toolbar zoom-in (`'in'`) or zoom-out (`'out'`) level from an explicit `current` zoom, one
 * additive step toward the zoom range's limit, rounded to 3 decimals. Null when `current` is
 * already at that limit or the step would not change the rounded level.
 */
export function computeToolbarZoomStep(current: number, direction: 'in' | 'out'): number | null {
  const rounded = +current.toFixed(3)

  if (direction === 'in' ? rounded >= CANVAS_ZOOM_MAX : rounded <= CANVAS_ZOOM_MIN) return null

  const next =
    direction === 'in'
      ? +Math.min(CANVAS_ZOOM_MAX, current + TOOLBAR_ZOOM_ADDITIVE_STEP).toFixed(3)
      : +Math.max(CANVAS_ZOOM_MIN, current - TOOLBAR_ZOOM_ADDITIVE_STEP).toFixed(3)

  if (+next.toFixed(3) === rounded) return null

  return next
}
